import { AdvancedSearchFieldModel } from "./advanced-search-field";
import { JurisdictionOverrideModel } from "./jurisdiction-override-model";
import { FacetModel } from "./facet";
import { SearchDownloadModel } from "./search-download";
import { AdvancedSearchModel } from "./advanced-search";
import "backbone-associations";
import "underscore";
import { AppSettings } from "../../app-settings";
import { prop } from "../../decorators/property";
import { autoinject, Container } from "aurelia-dependency-injection";
import { AutoCorrect } from "./../helpers/auto-correct";
import { ResultModel } from "../../search/models/result";
import { ResultsCollection } from "../../search/collections/results";
import { AdvancedSearchFormModel } from "../../search/models/advanced-search-form";
import { ExactResultModel } from "../../search/models/exact-result";
import { IRequestOptions } from "../../search/interfaces/request-options";
import { backboneModelProperties } from "../../decorators/backbone-properties";
import { AuthorizationModel } from "../../authentication/models/authorization-model";
import { AppEvents } from "../../app-events";
import { UserSettingsModel } from "../../settings/models/user-settings";
import { ApplicationInsights} from "../../shared/helpers/application-insights";
import { SearchTreeBuilder} from "../helpers/search-tree/search-tree-builder";
import { TreeNode} from "../helpers/search-tree/tree-node";
import { QueryComponentsCollection } from "../collections/query-components";
import { QueryComponentModel } from "../models/query-component";
import { IProductFilter } from "../interfaces/product-filter";
import { MobileHelper } from "../../shared/helpers/mobile-helper";
import { WelcomeModel } from "./welcome";
import { SearchAlertModel } from "./search-alert";

@autoinject
@backboneModelProperties({
    urlRoot: AppSettings.SearchApiUrlPrefix + "search",
    relations: [{
        type: Backbone.Many,
        key: "Results",
        relatedModel: ResultModel,
        collectionType: ResultsCollection
    },
        {
            type: Backbone.One,
            key: "ExactMatchResult",
            relatedModel: ExactResultModel
        },
        {
            type: Backbone.One,
            key: "AdvancedSearchForm",
            relatedModel: AdvancedSearchFormModel
        },
        {
            type: Backbone.Many,
            key: "CategoryFacets",
            relatedModel: FacetModel
        },
        {
            type: Backbone.Many,
            key: "MaterialFacets",
            relatedModel: FacetModel
        },
        {
            type: Backbone.Many,
            key: "MaterialTypes",
            relatedModel: FacetModel
        }
    ],
    defaults: {
        id: 1,
        Query: "",
        Page: 1,
        MaterialType: "",
        AvailableDate: "",
        Order: "Relevance",
        Reverse: false,
        ChangeTrigger: true,
        ExpandJurisdictions: false,
        AdvancedVisible: false,
        TotalResults: -1,
        ExactMatchResult: null,
        Results: [],
        AdvancedSearchForm: null,
        CategoryFacets: [],
        MaterialTypeCounts: [],
        MaterialFilters: [],
        MaterialFacets: [],
        Indicators: []
    }
})
export class SearchModel extends AuthorizationModel {
    @prop() public Loading: boolean;
    @prop() public Order: string;
    /** Returned from the server when its recognised a known case name or citation. */
    @prop() public HighlightableSearchFragments: QueryComponentModel[];
    /** Flag to indicate when the query has been changed or set, i.e if we search via URL */
    @prop() public QueryChanged = false;
    @prop() public Page: number;
    @prop() public TotalResults: number;
    @prop() public TotalUnfilteredResults: number;
    @prop() public Toastify: boolean;
    @prop() public Results: ResultsCollection;
    @prop() public AllResults = new ResultsCollection();
    @prop() public ExactMatchResult: ExactResultModel;
    @prop() public AdvancedVisible: boolean;
    @prop() public MiddlePagerEnter: boolean;
    /** Representation of all the category facets as models, including enabled/disbaled state etc */
    @prop() public CategoryFacets: Backbone.Collection<FacetModel>;
    @prop() public ExpandJurisdictionsRecommended: boolean;
    /** Material Type for this search. Not a filter but a restriction on the search via using one of the adv. forms */
    @prop() public MaterialType: string;
    @prop() public Indicators: any[];
    /** MaterialFilter facets, which can be used to filter the current results set */
    @prop() public MaterialFacets = new Backbone.Collection<FacetModel>();
    @prop() public AvailableDate: string;
    @prop() public CategoryFilters: string[];
    @prop() public ResultsOutsideJurisdictionSettings: number;
    @prop() public SpellCorrectionRecommended: boolean;
    @prop() public SpellingCorrection: string;
    /** Holds info for the search-box widget. 0 we aren't looking in a specific report */
    @prop() public NumberOfSeriesSearched: number;
    @prop() public ShowFulltextDocsOnly: boolean;
    /** Tree representation of the contents of the searchbox/advanced search form */
    @prop() public SearchTree: TreeNode;
    /** The tree of the current search (i.e last submitted search) This is needed for changing page/jurisdiction etc */
    @prop() public CurrentSearchTree: TreeNode;
        /** Search error message to be displayed below the search box - e.g malformed query */
    @prop() public ErrorMessage: string;
    @prop() public DownloadModel: SearchDownloadModel;
    @prop() public JurisdictionOverrides: Backbone.Collection<JurisdictionOverrideModel>;
    @prop() public AlertModel: SearchAlertModel;
    @prop() public Reference: string;
    @prop() public RavenQueryString: string;
    @prop() public DisableJurisdictionFiltering: boolean;
    @prop() public DisableMaterialFiltering: boolean;

    private searchRequest: XMLHttpRequest;
    private facetsRequest: XMLHttpRequest;

    public get QueryComponents(): QueryComponentsCollection {
        if (!this.SearchTree) { return null; }
        const collection = new QueryComponentsCollection();
        const dataObject = this.SearchTree.toDataObject();
        Object.keys(dataObject).forEach((key) => {
            const value = $.trim(dataObject[key]);
            if (key !== "Unrepresentable" && value) {
                const model = new QueryComponentModel();
                model.Fragment = value;
                model.FieldName = key;
                collection.add(model);
            }
        });
        return collection;
    }

    private validSortOrders = ["Relevance", "MostRecent", "Oldest", "Title", "Court"];

    constructor(private appEvents: Backbone.Events, private appInsights: ApplicationInsights, public AdvancedSearch: AdvancedSearchModel, public Welcome: WelcomeModel) {
        super();
        AdvancedSearch.SearchModel = this;
        this.DownloadModel = new SearchDownloadModel(this);
        this.AlertModel = new SearchAlertModel();
        this.setupEventListeners();
        this.createJurisdictionOverrideModels();
    }

    public createJurisdictionOverrideModels() {
        const oz = new JurisdictionOverrideModel(this, {Name: "Australia", Id: 177});
        const ca = new JurisdictionOverrideModel(this, {Name: "Canada", Id: 194});
        const cc = new JurisdictionOverrideModel(this, {Name: "Caribbean Community", Id: 9});
        const coe = new JurisdictionOverrideModel(this, {Name: "Council of Europe", Id: 598});
        const eu = new JurisdictionOverrideModel(this, {Name: "European Union", Id: 5});
        const ie = new JurisdictionOverrideModel(this, {Name: "Ireland", Id: 363});
        const nz = new JurisdictionOverrideModel(this, {Name: "New Zealand", Id: 264});
        const uk = new JurisdictionOverrideModel(this, {Name: "United Kingdom", Id: 285});

        const others = new JurisdictionOverrideModel(this, {Name: "Other Jurisdictions", Id: -1});

        const arr = [oz, ca, cc, coe, eu, ie, nz, uk, others];
        this.JurisdictionOverrides = new Backbone.Collection<JurisdictionOverrideModel>(arr);
    }

    public filterChangedReRunSearch() {
        if (this.SearchTree.toQueryString() !== this.CurrentSearchTree.toQueryString()) {
            this.SearchTree = this.CurrentSearchTree;
        }
        _.defer(() => {
            this.setPage(1);
            this.emitSearchRequest();
        });
    }

    public cleanOut() {
        this.AllResults.reset();
        this.CurrentSearchTree = null;
        this.MaterialType = "";
        this.Indicators = [];
        this.AvailableDate = "";
        this.Results.reset();
        this.DownloadModel.reset();
        this.AlertModel.clear();
        this.clearAllFilters();
        this.AdvancedSearch.clearAllFormsFields();
        this.AdvancedSearch.clearAllFormsFilters();
        this.AdvancedSearch.AdvancedSearchEverOpened = false;
        this.ShowFulltextDocsOnly = false;
        this.set(this.defaults);
    }

    public clearAllFilters() {
        this.MaterialFacets.forEach((m) => {
            m.Ticked = false;
        });
        this.CategoryFacets.forEach((m) => {
            m.Ticked = false;
        });
        this.resetJurisdictionOverrides();
        this.ShowFulltextDocsOnly = false;

        const elem = $("#filter-menu > a").first();
        elem.attr("title", "Filter Results");
    }

    public getQueryString(): string {
        return this.SearchTree ? this.SearchTree.toQueryString() : "";
    }

    public emitSearchRequest(replaceHistory?: boolean) {
        this.ErrorMessage = "";
        this.Toastify = false;
        const query = this.getQueryString();
        if ($.trim(query) === "") { return; }

        const options: IRequestOptions = {
            page: this.Page,
            query: query,
            order: this.Order,
            materialTypes: this.getActiveMaterialFilters(),
            includedCategoryFilters: this.CategoryFilters,
            replaceHistory: replaceHistory ? replaceHistory : false,
            includedProductFilters: this.IndicatorFilters,
            showFullTextOnly: this.ShowFulltextDocsOnly,
            availableDate: this.AvailableDate
        };

        this.appEvents.trigger(AppEvents.SearchRequest, options);

        if (Array.isArray(options.includedProductFilters) && options.includedProductFilters.length > 0) {
            const aiLog = {
                page: this.Page,
                query: query,
                order: this.Order,
                materialTypes: this.getActiveMaterialFilters(),
                includedCategoryFilters: this.CategoryFilters,
                replaceHistory: replaceHistory ? replaceHistory : false,
                includedProductFilters: this.getActiveProductFilters(),
                availableDate: this.AvailableDate
            };
            this.appEvents.trigger(AppEvents.AppInsightsSearchAdvanced, aiLog); // Log Advanced Search
        } else {
            const aiLog = {
                page: this.Page,
                query: query,
                order: this.Order,
                materialTypes: this.getActiveMaterialFilters(),
                includedCategoryFilters: this.CategoryFilters,
                replaceHistory: replaceHistory ? replaceHistory : false,
                availableDate: this.AvailableDate
            };
            this.appEvents.trigger(AppEvents.AppInsightsSearchStandard, aiLog); // Log Standard Search
        }
    }

      public setFilterTooltip(options: IRequestOptions) {
       let title = "";

       if (options.materialTypes && options.materialTypes.length > 0) {
           title += `Material Type: ${options.materialTypes.join(", ")}\n`;
       }

       const jurisdiction =  this.JurisdictionOverrides.filter((j) => {
                                return $.inArray(j.Id, this.getActiveJurisdictionOverrides()) > -1;
                             })
                             .map((x) => x.Name)
                             .join(", ");

       if (jurisdiction !== "") {
            title += `Jurisdiction: ${jurisdiction}\n`;
       }

       const categoryFilters = [];
       if (options.includedCategoryFilters && options.includedCategoryFilters.length > 0) {
            options.includedCategoryFilters.forEach((c) => categoryFilters.push(this.toTitleCase(c)));
            title += `Categories: ${categoryFilters.join(", ")}\n`;
       }

       if (options.showFullTextOnly) {
            title += `Showing full text only\n`;
       }

       if (title === "") {
           title = "Filter Results";
       }

       const elem = $("#filter-menu > a").first();
       elem.attr("title", title);
    }

    public  toTitleCase(str: string): string {
        return str.replace(/(?:^|\s)\w/g, (match) =>
            match.toUpperCase()
        );
    }

    public get IndicatorFilters(): IProductFilter[] {
        return (this.AdvancedSearch && this.AdvancedSearch.AdvancedSearchEverOpened) ? this.AdvancedSearch.AllProductFilters : null;
    }

    public areWeFilteringByIndicators(): boolean {
        return this.AdvancedSearch.areWeFilteringByProduct();
    }

    public async emitSearchRequestJurisdictionUpdate() {
        if (this.getQueryString() === "") { return; }
        this.Loading = true;
        this.resetJurisdictionOverrides();
        this.AllResults.reset(); // only needed for mobile, but it is needed.
        await this.getFromServer();
    }

    public categoryFacetChanged() {
        const selectedFacets = this.CategoryFacets.filter((f) => f.Ticked);
        const selectedNames = selectedFacets.map((m) => m.Name.toLowerCase());
        this.setCategoryFilters(selectedNames, false);
    }

    public async updateResults(customData?) {
        if (this.getQueryString() === "") {
            return;
        }
        await this.getFromServer(customData);
    }

    public constructSearchTree(query: string): void {
        this.SearchTree = SearchTreeBuilder.buildTreeFromQuery(query);
        this.QueryChanged = !this.QueryChanged;
    }

    public setOrder(order: string, silent: boolean): void {
        order = this.validateOrder(order);
        this.set({Order: order }, { silent: silent });
    }

    public setAvailableDate(availableDate: string, silent: boolean): void {
        this.set({AvailableDate: availableDate }, { silent: silent });
    }

    public getOrder(): string {
        return this.Order;
    }

    public getPage(): number {
        return this.Page;
    }

    public setPage(page: number, silent = false): void {
        const validatedPage = this.validatePage(page);
        this.set({Page: validatedPage }, { silent: silent });
        if (validatedPage !== page) {
            this.emitSearchRequest();
        }
        this.refreshNavBarUI();
    }

    public includeMaterialFilters(value: string[], silent: boolean) {
        if (this.MaterialFacets.length === 0)  {
            _.each(value, (v) => {
                this.MaterialFacets.add(new FacetModel({Name: v, Ticked: true})); // If we've not done any searches before the collection is empty, so populate
            });
        }

        this.MaterialFacets.forEach((m) => {
            if (m.HasSubFacets) {
                m.SubFacets.forEach((sf) => {
                    if (_.contains(value, sf.Name)) {
                        sf.Ticked = true;
                    } else {
                        sf.Ticked = false;
                    }
                });
                if (!m.SubFacets.any((x) => x.Ticked) || m.SubFacets.all((x) => x.Ticked)) {
                    m.PartiallyTicked = false;
                } else {
                    m.PartiallyTicked = true;
                }
            }

            if (_.contains(value, m.Name)) {
                m.Ticked = true;
            } else {
                m.Ticked = false;
            }
        });
    }

    public getMaterialCount(materialType: string): number {
        const counts = this.get("MaterialTypeCounts");
        const mapped  = _.map(counts, (m: any) => m.SubFacets ? [m].concat(m.SubFacets) : [m]); // turn each facet into an array of itself, and its subfacets (if any)
        const flatMapped = _.flatten(mapped); // Then flatten the array to get a simple list of all facets and subfacets

        const material = _.find(flatMapped, (obj: any) => obj.Name.toLowerCase() === materialType.toLowerCase());
        return (material === undefined) ? 0 : material.Count;
    }

    public includeCategoryFilters(value: string[]) {
        if (!this.CategoryFacets) {
            this.CategoryFacets = new Backbone.Collection<FacetModel>();
        }

        if (this.CategoryFacets.length === 0)  {
            _.each(value, (v) => {
                this.CategoryFacets.add(new FacetModel({Name: v, Ticked: true})); // If we've not done any searches before the collection is empty, so populate
            });
        }

        this.CategoryFacets.forEach((m) => {
            if (m.HasSubFacets) {
                m.SubFacets.forEach((sf) => {
                    if (_.contains(value, sf.Name)) {
                        sf.Ticked = true;
                    }
                });
            }
            if (_.contains(value, m.Name)) {
                m.Ticked = true;
            }
        });
    }

    public setCategoryFilters(value: string[], silent: boolean) {
        this.set({CategoryFilters: value }, { silent: silent });
    }

    public getCourtLabel(court: string): string {
        return court;
    }

    public getCategoryLabel(category: string): string {
        return category;
    }

    public resetJurisdictionOverrides() {
        this.JurisdictionOverrides.forEach((j) => {
            j.Ticked = false;
        });
    }

    public prepForNewSearch() {
        this.AllResults.reset();
        this.clearAllFilters();
        this.includeMaterialFilters([], false);
        this.setCategoryFilters([], true);
        this.DownloadModel.reset();
        this.AlertModel.clear();
        this.resetJurisdictionOverrides();
        this.setPage(1, false);
        this.setAvailableDate(null, false);
        this.HighlightableSearchFragments = [];
        this.AdvancedVisible = false;
    }

    public async AreWeSearchingAllJurisdictions() {
        const model: UserSettingsModel = Container.instance.get(UserSettingsModel);
        await model.loadSettings().then((resolve) => {
            return model.AllJurisdictionsSelected;
        }).catch((reject) => {
            reject();
        });
        return model.AllJurisdictionsSelected;
    }

    public async GetUserSettings() {
        const model: UserSettingsModel = Container.instance.get(UserSettingsModel);
        await model.loadSettings().then((resolve) => {
            return model;
        }).catch((reject) => {
            reject();
        });
        return model;
    }

    public getNumberOfResultsForCurrentMaterial(): number {
        return this.TotalResults;
    }

    public getActiveIndicators() {
        if (!this.AdvancedSearch || !this.AdvancedSearch.AllProductFilters) {
            return [];
        }

        const indicators = [];

        let allIncluded = true;

        this.AdvancedSearch.AllProductFilters.forEach((f) => {
            if (f.Included) {
                if (f.Indicators.length) {
                    f.Indicators.forEach((i) => {
                        indicators.push(i);
                    });
                }
            } else {
                allIncluded = false;
            }
        });

        if (allIncluded) { // all is the same as none
            return [];
        }

        return indicators;
    }

    public getActiveJurisdictionOverrides() {
        const ids = [];
        this.JurisdictionOverrides.forEach((j) => {
            if (j.Ticked) {
                ids.push(j.Id);
            }
        });
        return ids;
    }

    public assembleSearchQueryData(customData?: any) {
        const data = this.getSearchQueryData(customData);

        return JSON.stringify(data);
    }

    public getSearchQueryData(customData?: any) {
        const mh = Container.instance.get(MobileHelper);
        const rpp = mh.IsMobileMode ? 20 : 10;

        let data = {
            Query: this.getQueryString(),
            Page: this.getPage(),
            Order: this.Order,
            AvailableDate: this.AvailableDate,
            ResultsPerPage: rpp,
            MaterialType: this.MaterialType,
            MaterialFilters: this.getActiveMaterialFilters(),
            Indicators: this.Indicators,
            JurisdictionOverrides: this.getActiveJurisdictionOverrides(),
            CategoryFilters: this.CategoryFilters,
            FulltextAvailable: this.ShowFulltextDocsOnly,
            QueryComponents : this.QueryComponents.toJSON()
        };

        if (customData) {
            data = Object.assign(data, customData);
        }

        return data;
    }

    public getActiveMaterialFilters(): string[] {
        const activeFilters = this.MaterialFacets.where((m) => m.Ticked || m.PartiallyTicked);
        const names = [];

        activeFilters.forEach( (f) => {
            if (f.PartiallyTicked && f.SubFacets) {
                f.SubFacets.forEach((sf) => {
                    if (sf.Ticked) {
                        names.push(sf.Name);
                    }
                });
            } else if (f.Ticked) {
                names.push(f.Name);
                if (f.HasSubFacets) {
                    f.SubFacets.forEach((sf) => {
                        names.push(sf.Name);
                    });
                }
            }
        });
        return names;
    }

    public getActiveProductFilters(): string[] {
        let activeFilters: IProductFilter[];
        const names: string[] = [];

        if (this.IndicatorFilters) {
           activeFilters = this.IndicatorFilters.filter((m) => m.Included === true);
        }

        activeFilters.forEach( (f) => {
            names.push(f.Indicators.join());
        });

        return names;
    }

    public areWeFiltering() {
        if (!this.areWeFilteringByMaterial() &&
             !this.areWeFilteringByCategory() &&
             !this.areWeFilteringWithJurisdictionOverride() &&
             !this.ShowFulltextDocsOnly
            ) {
            return false;
        }

        return true;
    }
    public areWeFilteringByMaterial() {
        const matTypes = this.getActiveMaterialFilters();
        return !(matTypes === null || matTypes === undefined || !matTypes.length);
    }
    public areWeFilteringByCategory() {
        const cats = this.CategoryFilters;
        return !(cats === null || cats === undefined || !cats.length);
    }
    public areWeFilteringWithJurisdictionOverride() {
        const jOverrides = this.getActiveJurisdictionOverrides();
        return !(jOverrides === null || jOverrides === undefined || !jOverrides.length); // if length is 0 we're overriding by using everything so we should return true
    }

    public async getUserSettingsForServer() {
        await this.GetUserSettings();
        return JSON.stringify(UserSettingsModel.GetSettings());
    }

    private setupEventListeners() {
        this.on("change:Order", this.orderChanged);
        this.on("change:TotalResults", this.totalResultsChanged);

        this.listenTo(this.AdvancedSearch, "FilterChanged", this.includedFiltersChanged);
        this.listenTo(this.AdvancedSearch, "FilterChanged", this.disableFilterRelatedSearchFields);

        this.listenTo(this, "change:SearchTree", this.treeChanged);

        this.listenTo(this.AdvancedSearch, "change:AdvancedSearchEverOpened", this.advancedFormOpenedFirstTime);
        this.listenTo(this.DownloadModel, "change:checkboxesEnabled", this.toggleDownloadCheckboxes);

        this.listenTo(this.AllResults, "change:requestingLoad", this.loadMore);
        this.listenTo(this.CategoryFacets, "change:Ticked", this.categoryFacetChanged);
        this.listenTo(this.CategoryFacets, "add", this.categoryFacetChanged);
    }

    private disableFilterRelatedSearchFields() {
        // Get list of active indicators
        let includedIndicators: string[];
        this.AdvancedSearch.ActiveForm.SearchFilterGroups.forEach((filterGroup) => {
            filterGroup.Filters.forEach((filter) => {
                if (filter.Included) {
                    includedIndicators = _.union(includedIndicators, filter.Indicators);
                }
            });
        });

        this.AdvancedSearch.ActiveForm.SearchFields.models.forEach((searchField: AdvancedSearchFieldModel) => {
            if (searchField.ApplicableIndicators && searchField.ApplicableIndicators.length > 0) {
                if (_.intersection(this.getApplicableIndicators(searchField.ApplicableIndicators), includedIndicators).length === 0) {
                    searchField.setContents("");
                    searchField.PlaceHolderText = "See info for more details";
                    searchField.Disabled = true;
                    searchField.announceKeyPress(); // When clearing the field contents, we need this to update it in the searchbox
                } else {
                    searchField.PlaceHolderText = "";
                    searchField.Disabled = false;
                }
            }
        });
    }

    private getApplicableIndicators(indicators): string[] {
        // IE has ApplicableIndicators as an array. Even though our model says its a string.
        if (Array.isArray(indicators)) {
            return indicators;
        }
        return indicators.split(",");
    }

    private toggleDownloadCheckboxes() {
        this.AllResults.forEach((element: ResultModel) => {
            element.ShowDownloadCheckbox = this.DownloadModel.checkboxesEnabled;
            element.updateDisplayProperties();
            if (!this.DownloadModel.checkboxesEnabled) {
                element.SelectedForDownload = false;
            }
        });
        this.AllResults.trigger("render");
    }

    private async loadMore() {
        if (this.Loading) {
            return false;
        }
        this.Page = this.Page + 1;
        await this.getFromServer();
    }

    private treeChanged() {
        if (this.ErrorMessage) { // We don't want the message to appear as soon as there's any syntax error, otherwise errors would show every time people typed quotes or brackets
            if (this.SearchTree.getError() !== this.ErrorMessage) {
                this.ErrorMessage = this.SearchTree.getError(); // We do need to check however that if there's a different error now (or no error) then to reflect that in the UI
            }
        }
    }

    private advancedFormOpenedFirstTime() {
        this.includedFiltersChanged();
        this.trigger("change:NumberOfSeriesSearched", this, {});
    }

    private includedFiltersChanged() {
        this.NumberOfSeriesSearched = this.AdvancedSearch.IncludedFiltersCount; // can probably strip this out of this model entirely, seems like a unecessary middleman
        this.trigger("change:NumberOfSeriesSearched", this, {});
    }

    private validateOrder(order: string): string {
        if (!order || !_.contains(this.validSortOrders, order)) {
            order = "Relevance";
        }
        return order;
    }

    private validatePage(page: number): number {
        let newPage = -1;
        if (!page || isNaN(page) || page < 1) {
            newPage = 1;
        }
        if (page > 1000000000) { // prevents search server throwing an error
            newPage = 999999999;
        }
        return (newPage === -1) ? page : newPage;
    }

    private setPageOne() {
        this.setPage(1, false);
    }

    private queryChanged() {
        this.setPageOne();
    }

    private orderChanged() {
        this.setPageOne();
        this.refreshNavBarUI();
    }

    private totalResultsChanged() {
        if (this.Page > Math.ceil(this.TotalResults / 10) && this.TotalResults > 0) { // Check positive total results so we don't infinite loop if server throws error and gives 0 results
            this.setPage(Math.ceil(this.TotalResults / 10), false);
            this.emitSearchRequest();
        }

        this.refreshNavBarUI();
        this.appEvents.trigger(AppEvents.TotalSearchResults, this.TotalResults);
    }

    /**
     * Sets a flag to let the view know to update NavBar UI.
     */
    private refreshNavBarUI() {
        this.trigger("UpdateNavBar");
    }

    private async handleError(response) {
        this.appEvents.trigger(AppEvents.CancelSuggestions);
        this.Loading = false;
        // Ignore aborts as this will happen when changing jurisdictions
        if (response.error().statusText === "abort") {
            return;
        }
        this.appInsights.trackException(response.error());
        if (response.status === 500) {
            this.appEvents.trigger(AppEvents.ErrorMessage, "Could not connect to server");
        } else {
            const options: any = { ErrorMessage: response.error().statusText };
            this.trigger(AppEvents.SearchServerRequestFailed, options);                }
    }

    private async getFromServer(customData?): Promise<any> {
        if (this.searchRequest) {
            this.searchRequest.abort();
            this.searchRequest = null;
        }
        if (this.facetsRequest) {
            this.facetsRequest.abort();
            this.facetsRequest = null;
        }

        this.CurrentSearchTree = this.SearchTree; // Save the search tree so that when filtering the search it can be on this search not on whatever is in the search box.

        const assembledData = this.assembleSearchQueryData(customData);

        const url = this.urlRoot + "/1";
        const data = assembledData;
        this.searchRequest = $.ajax({
            type: "POST",
            url: url,
            headers: {
                UserSettings: await this.getUserSettingsForServer()
            },
            beforeSend: this.attachAuthorizationHeader,
            cache: false,
            data: data,
            contentType: "application/json",
            dataType: "json",
            success: (responseData) => this.handleSuccess(assembledData, responseData),
            error: (responseData) => this.handleError(responseData)
        });
    }

    private handleSuccess(requestData, responseData) {
        this.appEvents.trigger(AppEvents.CancelSuggestions);
        this.set(responseData);
        const mh: MobileHelper = Container.instance.get(MobileHelper);
        if (!mh.IsMobileMode) {
            this.AllResults.reset(); // On desktop we paginate results, on mobile we append to collection
            this.handleRecommendations();
        }

        this.refreshNavBarUI();
        this.trigger("ShowOrHideResultsPane");
        this.Loading = false;

        if (responseData.Results && responseData.Results.length > 0) {
            this.appEvents.trigger(AppEvents.SearchCompleted, {request: JSON.parse(requestData), response: responseData});
            responseData.Results[responseData.Results.length - 1].isWaypoint = true;
            responseData.Results.forEach((m) => {
                const model = new ResultModel(m);
                model.ShowDownloadCheckbox = this.DownloadModel.checkboxesEnabled;
                model.SelectedForDownload = this.DownloadModel.isThisMarkedForDownload(model.Id); // if we change page and come back we want selected ones to stay selected
                this.listenTo(model, "change:SelectedForDownload", (e) => {
                    const added = this.DownloadModel.addOrRemoveDocKey(e);
                    if (!added) { model.SelectedForDownload = false; }
                });
                this.AllResults.push(model);
            });

            if (responseData.ExactMatchResult) {
                this.ExactMatchResult = responseData.ExactMatchResult;
            }
        }

        this.announceResults();

        if (!this.ErrorMessage) {
            this.getFacetsFromServer();
        }
    }

    private announceResults() {
        const totalPages = Math.ceil(this.TotalResults / 10);

        switch (this.TotalResults) {
            case -1:    // Search data has been emptied
            case undefined:
            case 0:
                this.appEvents.trigger(AppEvents.Announcement, "No results found");
                break;
            default:   // Got results
                let announcementText = "Showing page " + this.Page + " of " + totalPages;
                if (this.Page === 1) {
                    announcementText = "Total results " + this.TotalResults + ". " + announcementText;
                }
                this.appEvents.trigger(AppEvents.Announcement, announcementText);
        }
    }

    private async getFacetsFromServer() {
        if (this.facetsRequest) {
            this.facetsRequest.abort();
            this.facetsRequest = null;
        }

        const assembledData = this.assembleSearchQueryData();

        this.facetsRequest = $.ajax({
            type: "POST",
            url: `${AppSettings.SearchApiUrlPrefix}SearchFacets`,
            headers: {
                UserSettings: await this.getUserSettingsForServer()
            },
            beforeSend: this.attachAuthorizationHeader,
            data: assembledData,
            contentType: "application/json",
            cache: false,
            dataType: "json",
            success: (data) => this.handleFacetsSuccess(data),
        });
    }

    private handleFacetsSuccess(data) {
        this.set(data);

        this.MaterialFacets.forEach((m) => {
            m.searchModel = this;
            if (m.HasSubFacets) {
                const first = m.SubFacets.first().Ticked;
                m.SubFacets.forEach((sf) => {
                    if (sf.Ticked !== first) {
                        m.PartiallyTicked = true;
                        m.Ticked = true;
                    }
                    sf.searchModel = this;
                });
            }
            m.Enabled = m.Count > 0 || m.Ticked || m.PartiallyTicked;
        });

        if (this.CategoryFacets) {
            this.CategoryFacets.forEach((c) => {
                c.searchModel = this;
            });
        }

        this.refreshNavBarUI();
    }

    private async handleRecommendations() {
        if (this.SpellCorrectionRecommended) {
            try {
                this.SpellingCorrection = await AutoCorrect.GetTitleCorrection(this.QueryComponents);
            } catch (err) {
                // do nothing
            }
        } else {
            this.SpellingCorrection = "";
        }

        if (this.Results && this.Results.length > 0) {
            let correctionResult;
            if (this.SpellCorrectionRecommended && this.SpellingCorrection) {
                correctionResult = new ResultModel({Title: this.SpellingCorrection, DocTypeString: "CORRECTION"});
            } else if (this.ExpandJurisdictionsRecommended) {
                correctionResult = new ResultModel({Title: "", DocTypeString: "EXPAND_JURISDICTIONS"});
            }
            console.log("ADD TO RESULTS");
            this.AllResults.add(correctionResult, {at: 0});
        }
    }
}



// WEBPACK FOOTER //
// ./~/tslint-loader??ref--0!./src/search/models/search.ts